import React from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { FileCheck2, KeyRound, Moon, Sun, UserRound, UserRoundCheck } from "lucide-react";
import EventChip, { type ChipTone } from "@/components/shared/EventChip";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useThemeStore } from "@/store/themeStore";
import BrandMark from "./BrandMark";

interface OnboardingLayoutProps {
  children: React.ReactNode;
  step?: number;
  totalSteps?: number;
  title?: string;
  subtitle?: string;
  fieldProgress?: number; // New prop for granular progress
}

const checklist = [
  { k: "identity", v: "Profile details captured" },
  { k: "documents", v: "Verification documents uploaded" },
  { k: "review", v: "Reviewed by the Taskpart team" },
  { k: "access", v: "Workspace unlocked" },
];

const checklistTones: ChipTone[] = ["lavender", "peach", "sky", "mint"];
const checklistIcons = [<UserRound key="i" />, <FileCheck2 key="d" />, <UserRoundCheck key="r" />, <KeyRound key="a" />];

export default function OnboardingLayout({
  children,
  step = 0,
  totalSteps = 1,
  title,
  subtitle,
  fieldProgress,
}: OnboardingLayoutProps) {
  // Use fieldProgress if provided, otherwise fallback to step-based
  const progress = fieldProgress !== undefined ? fieldProgress : ((step + 1) / totalSteps) * 100;
  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  return (
    <div className="grid min-h-dvh bg-background text-foreground lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
      {/* Left panel: context (desktop only) */}
      <aside className="hidden p-3 lg:flex">
        <div className="relative flex w-full flex-col justify-between overflow-hidden rounded-3xl bg-shell p-12 xl:p-14">
          <div aria-hidden className="pointer-events-none absolute -top-32 -left-24 size-[400px] rounded-full bg-pastel-lavender opacity-60 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -right-24 -bottom-40 size-[380px] rounded-full bg-pastel-mint opacity-50 blur-3xl" />
          <div className="relative">
            <Link
              to="/"
              aria-label="Taskpart home"
              className="inline-block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <BrandMark />
            </Link>
            <span className="mt-16 mb-5 flex w-fit items-center gap-2 rounded-full bg-card px-3 py-1 text-xs font-medium text-body shadow-soft">
              <span className="size-1.5 rounded-full bg-foreground" aria-hidden />
              Onboarding
            </span>
            <h2 className="max-w-md text-[44px] leading-[50px] font-medium tracking-[-1.4px] text-foreground">
              {title || "DevFlow Onboarding"}
            </h2>
            <p className="mt-5 max-w-md text-body-md text-body">
              {subtitle ||
                "Complete your profile to unlock premium industrial placements and professional networking."}
            </p>
          </div>

          <ol className="relative my-10 space-y-2.5 rounded-2xl border border-border bg-card p-4 shadow-panel">
            {checklist.map((line, i) => (
              <li key={line.k} className="flex items-center gap-3">
                <span className="w-6 shrink-0 text-xs text-muted-foreground tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <EventChip
                  tone={checklistTones[i]}
                  icon={checklistIcons[i]}
                  title={line.k.charAt(0).toUpperCase() + line.k.slice(1)}
                  meta={line.v}
                  className="flex-1"
                />
              </li>
            ))}
          </ol>

          <p className="relative border-t border-hairline-strong/60 pt-6 text-xs text-muted-foreground">
            Professional ecosystem · 2026
          </p>
        </div>
      </aside>

      {/* Right panel: content / form */}
      <div className="flex min-h-dvh flex-col">
        <header className="flex h-16 items-center justify-between px-5 sm:px-8">
          <Link
            to="/"
            aria-label="Taskpart home"
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:invisible"
          >
            <BrandMark />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label={mode === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            className="rounded-full border border-border text-body hover:text-foreground"
          >
            {mode === "dark" ? <Sun /> : <Moon />}
          </Button>
        </header>

        <main className="flex flex-1 flex-col items-center justify-center px-5 pt-6 pb-12 sm:px-8 lg:px-12">
          <div className="w-full max-w-[540px]">
            {(title || subtitle) && (
              <div className="mb-8 lg:hidden">
                {title && <p className="eyebrow mb-2">{title}</p>}
                {subtitle && <p className="text-sm text-body">{subtitle}</p>}
              </div>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                {children}
              </motion.div>
            </AnimatePresence>

            {totalSteps > 1 && (
              <div className="mt-10 border-t border-border pt-6">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="eyebrow">Profile calibration</span>
                  <span className="text-caption text-muted-foreground tabular-nums">
                    <span className="text-foreground">{Math.round(progress)}%</span>
                    <span className="ml-2">
                      Step {step + 1} of {totalSteps}
                    </span>
                  </span>
                </div>
                <Progress
                  value={progress}
                  className="h-2"
                  aria-label="Onboarding progress"
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
